import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom'; // Import Link and useSearchParams
import { supabase } from '../../supabaseClient'; // Adjust path if necessary
import { setDeviceToken, getDeviceName, normalizePhone } from '../../lib/deviceTrust';
import { useAuth } from '../../context/AuthContext';
import SMSVerification from './SMSVerification';

// Interface for validated invite data
interface InviteDetails {
  invitation_id: string;
  organization_id: string;
  role: string;
  email: string;
}

const SignUpForm: React.FC = () => {
  const { hasSafetyModules } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialToken = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false); // State for checkbox
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // State for invitation handling
  const [inviteToken, setInviteToken] = useState<string | null>(initialToken);
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [isValidatingToken, setIsValidatingToken] = useState<boolean>(!!initialToken);
  const [tokenValidationError, setTokenValidationError] = useState<string | null>(null);

  // New fields + SMS verification state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [showSMS, setShowSMS] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  // Effect to validate token on load
  useEffect(() => {
    if (inviteToken) {
      setIsValidatingToken(true);
      setTokenValidationError(null);
      setMessage(null); // Clear previous messages
      setError(null);

      supabase.functions.invoke('validate-invite-token', {
        body: { token: inviteToken },
      })
      .then(({ data, error: validationError }) => {
        if (validationError) {
          console.error("Token validation error:", validationError);
          setTokenValidationError(validationError.message || "Invalid or expired invitation token.");
          setInviteToken(null); // Clear invalid token
        } else if (data && data.organization_id) {
          setInviteDetails(data as InviteDetails);
          setEmail(data.email); // Pre-fill email
          setMessage("Invitation validated. Please complete your sign up.");
        } else {
           console.error("Token validation failed: No data returned.");
           setTokenValidationError("Invalid invitation token response.");
           setInviteToken(null);
        }
      })
      .catch((err) => {
        console.error("Unexpected error validating token:", err);
        setTokenValidationError("An unexpected error occurred during token validation.");
        setInviteToken(null);
      })
      .finally(() => {
        setIsValidatingToken(false);
      });
    } else {
      setIsValidatingToken(false); // No token to validate
    }
  }, [inviteToken]); // Re-run if inviteToken changes (though it shouldn't after initial load)

  // F3: capture Connect returnTo and stash in sessionStorage so it survives
  // the Stripe checkout redirect round-trip.
  useEffect(() => {
    const returnTo = searchParams.get('returnTo');
    if (returnTo && returnTo.startsWith('/connect/accept/')) {
      try {
        sessionStorage.setItem('connectReturnTo', returnTo);
      } catch { /* storage unavailable — fall through to default redirect */ }
    }
  }, [searchParams]);

  // F3: pull Connect returnTo (if present) and clear it. Only honors the allowlist prefix.
  const consumeConnectReturnTo = (): string | null => {
    try {
      const v = sessionStorage.getItem('connectReturnTo');
      if (v && v.startsWith('/connect/accept/')) {
        sessionStorage.removeItem('connectReturnTo');
        return v;
      }
    } catch { /* storage unavailable */ }
    return null;
  };

  const handleSMSVerified = async (deviceToken: string) => {
    try {
      if (pendingUserId) {
        await supabase
          .from('profiles')
          .update({ first_name: firstName, last_name: lastName, phone: smsPhone })
          .eq('id', pendingUserId);
      }

      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      await supabase.from('trusted_devices').insert({
        user_id: pendingUserId,
        device_token: deviceToken,
        device_name: getDeviceName(),
        organization_id: pendingOrgId,
        expires_at: thirtyDaysFromNow.toISOString(),
      });

      setDeviceToken(deviceToken);
    } catch (err) {
      console.error('Failed to complete post-verification setup:', err);
      setDeviceToken(deviceToken);
    } finally {
      setShowSMS(false);
      const connectReturn = consumeConnectReturnTo();
      navigate(connectReturn || (hasSafetyModules ? '/dashboard' : '/connected-orgs'));
    }
  };

  const handleSMSCancel = async () => {
    // Save name fields even without phone verification
    if (pendingUserId) {
      try {
        await supabase
          .from('profiles')
          .update({ first_name: firstName, last_name: lastName })
          .eq('id', pendingUserId);
      } catch {}
    }
    setShowSMS(false);
    const connectReturn = consumeConnectReturnTo();
    navigate(connectReturn || (hasSafetyModules ? '/dashboard' : '/connected-orgs'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if terms are agreed
    if (!agreedToTerms) {
      setError('You must agree to the terms and policies to sign up.');
      return;
    }

    // TEMPORARY: Phone field hidden until 10DLC approved. Re-enable by uncommenting.
    // See: Twilio 10DLC campaign re-review in progress as of April 2026.
    // const normalizedPhone = normalizePhone(phone);
    // if (!normalizedPhone) {
    //   setError('Please enter a valid 10-digit US phone number.');
    //   return;
    // }

    // Organic (token-less) signup is retired — SignUpPage redirects those
    // visitors to /get-started before this form renders. Reaching here without
    // a validated invitation means the invite token failed validation; stop
    // before auth.signUp so no orphan auth user (account with no profile) is
    // created.
    if (!inviteDetails) {
      setError('This invitation link is invalid or has expired. Ask your administrator to resend the invitation, or visit /get-started to create a new organization.');
      return;
    }

    setLoading(true);
      setError(null);
      setMessage(null);
      setTokenValidationError(null); // Clear token error on new submit attempt

      // If it's an invite flow, ensure the email hasn't been changed
      if (inviteDetails && email !== inviteDetails.email) {
        setError("The email address cannot be changed when signing up via invitation.");
        return;
      }

    try {
      // 1. Sign up the user in Supabase Auth
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (signUpError) {
        // Handle specific errors like "User already registered"
        if (signUpError.message.includes("User already registered")) {
           setError("An account with this email already exists. Please log in instead.");
        } else {
           throw signUpError; // Throw other errors to be caught below
        }
        return; // Stop execution if sign up failed
      }

      if (data.user) {
        // User created in auth.users, now create profile

        let organizationId: string | null = null;
        let roleToAssign: string = 'USER'; // Default role for standard signup
        let currentInvitationId: string | null = null;

        // Determine organization_id and role based on validated invite details or defaults
        if (inviteDetails) {
          // Use details from the validated token
          organizationId = inviteDetails.organization_id;
          roleToAssign = inviteDetails.role;
          currentInvitationId = inviteDetails.invitation_id; // Store for the accept step
        }
        // No else: organic signup retired. The !inviteDetails guard above
        // returns before auth.signUp, and the !organizationId check below
        // fails closed if this is ever reached without invite details.

        // Ensure we have an organization ID before proceeding
        if (!organizationId) {
             console.error('Organization ID is null, cannot create profile.');
             throw new Error('Failed to determine organization for profile creation.');
        }

        // 2. Create profile using RPC function to bypass RLS timing issues
        const { error: profileError } = await supabase
          .rpc('create_user_profile', {
            user_id: data.user.id,
            user_email: data.user.email,
            org_id: organizationId,
            user_role: roleToAssign
          });

        if (profileError) {
          console.error('Error inserting profile:', profileError);
          // Attempt to clean up the auth user if profile insertion fails (best effort)
          // This requires admin rights, so ideally call another Edge Function
          // For now, just log and throw the profile error
          throw new Error('Unable to create account. Please try again or contact support.');
        }

        // 3. If this was an invite, call the accept-invite function
        if (currentInvitationId) {
          const { error: acceptError } = await supabase.functions.invoke('accept-invite', {
            body: { invitation_id: currentInvitationId },
          });

          if (acceptError) {
            // Log the error but don't necessarily fail the whole signup process,
            // as the user and profile are already created.
            console.error(`Error marking invitation ${currentInvitationId} as accepted:`, acceptError);
            // Optionally: Set a specific message indicating this issue?
          }
        }

        // TEMPORARY: Phone field hidden until 10DLC approved. Re-enable by uncommenting.
        // See: Twilio 10DLC campaign re-review in progress as of April 2026.
        // --- ORIGINAL SMS verification + device trust (re-enable when 10DLC approved) ---
        // if (data.session) {
        //   try {
        //     await supabase.functions.invoke('verify-phone-send', {
        //       body: { phone: normalizedPhone },
        //     });
        //
        //     setSmsPhone(normalizedPhone);
        //     setPendingUserId(data.user.id);
        //     setPendingOrgId(organizationId);
        //     setShowSMS(true);
        //     return; // Show SMS screen instead of navigating
        //   } catch (smsErr) {
        //     console.error('Failed to send SMS OTP during signup:', smsErr);
        //     // Fail-open: save profile fields and navigate normally
        //     try {
        //       await supabase
        //         .from('profiles')
        //         .update({ first_name: firstName, last_name: lastName, phone: normalizedPhone })
        //         .eq('id', data.user.id);
        //     } catch {}
        //     navigate('/dashboard');
        //   }
        // } else {
        //   setMessage('Sign up successful! You can now log in.');
        // }
        // --- END ORIGINAL ---

        // 4. Save profile name fields and go straight to dashboard (no phone/SMS)
        if (data.session) {
          try {
            await supabase
              .from('profiles')
              .update({ first_name: firstName, last_name: lastName })
              .eq('id', data.user.id);
          } catch {}
          const connectReturn = consumeConnectReturnTo();
          navigate(connectReturn || (hasSafetyModules ? '/dashboard' : '/connected-orgs'));
        } else {
          setMessage('Sign up successful! You can now log in.');
        }

      } else {
        // This case should ideally not happen if signUpError is handled correctly
        setError('An unexpected issue occurred during sign up (no user data returned).');
      }
    } catch (error: any) {
      // Use the specific error message if available, otherwise use a generic one
      setError(error.message || 'An unexpected error occurred during sign up. Please try again.');
      console.error('Sign up process error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (showSMS) {
    return (
      <SMSVerification
        phone={smsPhone}
        onVerified={handleSMSVerified}
        onCancel={handleSMSCancel}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Display general errors */}
      {error && (
        <div className="p-3 text-sm text-status-danger bg-status-danger-light border border-status-danger/30 rounded-lg">
          {error}
        </div>
      )}
      {/* Display success messages */}
      {message && !error && ( // Only show message if no error
        <div className="p-3 text-sm text-status-success bg-status-success-light border border-status-success rounded-lg">
          {message}
        </div>
      )}
       {/* Display token validation status */}
       {isValidatingToken && (
         <div className="p-3 text-sm text-accent-blue bg-accent-blue-wash border border-accent-blue rounded-lg">
           Validating invitation token...
         </div>
       )}
       {tokenValidationError && (
         <div className="p-3 text-sm text-status-danger bg-status-danger-light border border-status-danger/30 rounded-lg">
           Token Error: {tokenValidationError}
         </div>
       )}

      <div>
        <label htmlFor="first-name" className="block text-sm font-medium text-text-body">
          First Name
        </label>
        <div className="mt-1">
          <input
            id="first-name"
            name="first-name"
            type="text"
            autoComplete="given-name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="block w-full px-4 py-3 placeholder-text-muted border border-surface-border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-accent-blue focus:border-accent-blue sm:text-sm"
            placeholder="First Name"
            disabled={loading}
          />
        </div>
      </div>

      <div>
        <label htmlFor="last-name" className="block text-sm font-medium text-text-body">
          Last Name
        </label>
        <div className="mt-1">
          <input
            id="last-name"
            name="last-name"
            type="text"
            autoComplete="family-name"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="block w-full px-4 py-3 placeholder-text-muted border border-surface-border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-accent-blue focus:border-accent-blue sm:text-sm"
            placeholder="Last Name"
            disabled={loading}
          />
        </div>
      </div>

      {/* TEMPORARY: Phone field hidden until 10DLC approved. Re-enable by uncommenting.
         See: Twilio 10DLC campaign re-review in progress as of April 2026.
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-text-body">
          Phone Number
        </label>
        <div className="mt-1">
          <input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="block w-full px-4 py-3 placeholder-text-muted border border-surface-border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-accent-blue focus:border-accent-blue sm:text-sm"
            placeholder="+1 (XXX) XXX-XXXX"
            disabled={loading}
          />
        </div>
      </div>
      */}

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-text-body"
        >
          Email address
        </label>
        <div className="mt-1">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`block w-full px-4 py-3 placeholder-text-muted border border-surface-border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-accent-blue focus:border-accent-blue sm:text-sm ${inviteDetails ? 'bg-surface-bg cursor-not-allowed' : ''}`}
            placeholder="you@example.com"
            disabled={loading || !!inviteDetails} // Disable if loading or if email is pre-filled from invite
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-text-body"
        >
          Password
        </label>
        <p className="mt-1 text-xs text-text-muted">
          Must be at least 8 characters long and include uppercase, lowercase, and a number.
        </p>
        <div className="mt-1">
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
            title="Password must be at least 8 characters long and include uppercase, lowercase, and a number."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="block w-full px-4 py-3 placeholder-text-muted border border-surface-border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-accent-blue focus:border-accent-blue sm:text-sm"
            placeholder="Password"
            disabled={loading}
          />
        </div>
      </div>

      {/* Terms Agreement Checkbox */}
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id="terms-agreement"
            name="terms-agreement"
            type="checkbox"
            required
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="w-4 h-4 text-accent-blue border-surface-border rounded focus:ring-accent-blue"
            disabled={loading}
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor="terms-agreement" className="text-text-body">
            I have read and agree to the{' '}
            <Link to="/license" target="_blank" className="font-medium text-accent-blue hover:text-accent-blue">
              Licensing Agreement
            </Link>
            ,{' '}
            <Link to="/privacy" target="_blank" className="font-medium text-accent-blue hover:text-accent-blue">
              Privacy Policy
            </Link>
            , and{' '}
            <Link to="/terms" target="_blank" className="font-medium text-accent-blue hover:text-accent-blue">
              Terms of Service
            </Link>
            . (Required)
          </label>
        </div>
      </div>

      <div>
        <button
          type="submit"
          // Disable if loading, terms not agreed, validating token, or token is invalid
          disabled={loading || !agreedToTerms || isValidatingToken || (!!inviteToken && !inviteDetails)}
          className="flex justify-center w-full px-4 py-3 text-sm font-bold text-white bg-accent-blue border border-transparent rounded-lg shadow-sm hover:bg-accent-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue disabled:opacity-50"
        >
          {loading ? 'Processing...' : (inviteDetails ? 'Accept Invite & Sign Up' : 'Sign Up')}
        </button>
      </div>
    </form>
  );
};

export default SignUpForm;

// Add empty export to satisfy '--isolatedModules'
export {};
